// Install Excel Add-in Page
// Guides users through installing the Calculom Excel add-in via various methods

import { useState, useMemo } from 'react';

// ============================================================
// PLATFORM DETECTION
// ============================================================

type Platform = 'windows' | 'mac' | 'unknown';

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('win')) return 'windows';
  if (ua.includes('mac')) return 'mac';
  return 'unknown';
}

// ============================================================
// CONSTANTS
// ============================================================

const MANIFEST_URL =
  import.meta.env.VITE_ADDIN_MANIFEST_URL ??
  `${window.location.origin}/addin/manifest.xml`;

// ============================================================
// ACCORDION CARD
// ============================================================

function AccordionCard({
  title,
  badge,
  badgeColor,
  children,
  defaultOpen = false,
}: {
  title: string;
  badge?: string;
  badgeColor?: 'green' | 'blue' | 'gray';
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  const badgeClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="card">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {badge && badgeColor && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClasses[badgeColor]}`}
            >
              {badge}
            </span>
          )}
        </div>
        <svg
          className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="mt-4 border-t pt-4">{children}</div>}
    </div>
  );
}

// ============================================================
// COPY BUTTON
// ============================================================

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <button
      onClick={handleCopy}
      className="btn-secondary text-sm"
    >
      {copied ? 'Copied!' : 'Copy URL'}
    </button>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

export function InstallAddinPage() {
  const platform = useMemo(detectPlatform, []);

  async function handleDownloadManifest() {
    try {
      const response = await fetch(MANIFEST_URL);
      if (!response.ok) throw new Error('Failed to fetch manifest');
      const text = await response.text();
      const blob = new Blob([text], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'calculom-manifest.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: direct link with download attribute
      const link = document.createElement('a');
      link.href = MANIFEST_URL;
      link.download = 'calculom-manifest.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Install Excel Add-in</h1>
        <p className="mt-1 text-gray-600">
          Choose an installation method below to get the Calculom add-in running in Excel.
          {platform !== 'unknown' && (
            <span className="ml-1 text-sm text-gray-500">
              (Detected: {platform === 'windows' ? 'Windows' : 'macOS'})
            </span>
          )}
        </p>
      </div>

      {/* Manifest URL */}
      <div className="card">
        <h2 className="font-medium text-gray-900 mb-3">Add-in Manifest URL</h2>
        <p className="text-sm text-gray-600 mb-3">
          Most installation methods require the manifest URL. Copy it or download the file.
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <code className="flex-1 font-mono text-sm bg-gray-100 px-3 py-2 rounded-lg break-all text-gray-800">
            {MANIFEST_URL}
          </code>
          <div className="flex gap-2">
            <CopyButton text={MANIFEST_URL} />
            <button onClick={handleDownloadManifest} className="btn-secondary text-sm">
              Download Manifest
            </button>
          </div>
        </div>
      </div>

      {/* Installation Methods */}
      <div className="space-y-3">
        <h2 className="font-medium text-gray-900">Installation Methods</h2>

        {/* 1. Sideloading */}
        <AccordionCard
          title="1. Sideloading"
          badge="Recommended"
          badgeColor="green"
          defaultOpen
        >
          <p className="text-sm text-gray-600 mb-4">
            Best for individual users who want to get started quickly. Works with Excel desktop and Excel on the web.
          </p>

          {platform === 'mac' ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">macOS Instructions</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                <li>Download the manifest file using the button above.</li>
                <li>
                  Open Finder and navigate to:{' '}
                  <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    ~/Library/Containers/com.microsoft.Excel/Data/Documents/wef
                  </code>
                </li>
                <li>If the <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">wef</code> folder doesn't exist, create it.</li>
                <li>Copy the downloaded manifest file into the <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">wef</code> folder.</li>
                <li>Open Excel, then go to the <strong>Insert</strong> tab and click <strong>My Add-ins</strong>.</li>
                <li>The Calculom add-in should appear under <strong>Developer Add-ins</strong>. Click it to load.</li>
              </ol>
            </div>
          ) : (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Windows Instructions</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                <li>Download the manifest file using the button above.</li>
                <li>
                  Open File Explorer and navigate to:{' '}
                  <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    \\%LOCALAPPDATA%\Microsoft\Office\16.0\Wef\
                  </code>
                </li>
                <li>If the <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Wef</code> folder doesn't exist, create it.</li>
                <li>Copy the downloaded manifest file into the <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Wef</code> folder.</li>
                <li>Open Excel, then go to the <strong>Insert</strong> tab and click <strong>My Add-ins</strong>.</li>
                <li>Click <strong>Shared Folder</strong> at the top of the dialog. Select Calculom and click <strong>Add</strong>.</li>
              </ol>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Excel on the Web:</strong> Go to <strong>Insert &gt; Office Add-ins &gt; Upload My Add-in</strong>,
                  then browse to the downloaded manifest file.
                </p>
              </div>
            </div>
          )}
        </AccordionCard>

        {/* 2. Centralized Deployment */}
        <AccordionCard
          title="2. Centralized Deployment"
          badge="For IT Admins"
          badgeColor="blue"
        >
          <p className="text-sm text-gray-600 mb-4">
            Deploy the add-in to all users in your Microsoft 365 organization via the admin center.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              Sign in to the{' '}
              <a
                href="https://admin.microsoft.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Microsoft 365 admin center
              </a>.
            </li>
            <li>Navigate to <strong>Settings &gt; Integrated apps</strong>.</li>
            <li>Click <strong>Upload custom apps</strong>.</li>
            <li>Select <strong>"Provide link to manifest file"</strong> and paste the manifest URL from above.</li>
            <li>Click <strong>Validate</strong>, then choose which users or groups should receive the add-in.</li>
            <li>Click <strong>Deploy</strong>. Users will see the add-in in Excel within 24 hours (often sooner).</li>
          </ol>
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> You need Microsoft 365 admin privileges to use centralized deployment.
            </p>
          </div>
        </AccordionCard>

        {/* 3. SharePoint App Catalog */}
        <AccordionCard title="3. SharePoint App Catalog" badge="Enterprise" badgeColor="gray">
          <p className="text-sm text-gray-600 mb-4">
            Host the add-in manifest in your organization's SharePoint app catalog for controlled distribution.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>Download the manifest file using the button above.</li>
            <li>Go to your SharePoint App Catalog site (usually <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">https://yourtenant.sharepoint.com/sites/appcatalog</code>).</li>
            <li>Navigate to <strong>Apps for Office</strong>.</li>
            <li>Upload the manifest XML file.</li>
            <li>Users can then find the add-in via <strong>Insert &gt; My Add-ins &gt; My Organization</strong> in Excel.</li>
          </ol>
        </AccordionCard>

        {/* 4. Direct Download + Manual Upload */}
        <AccordionCard title="4. Direct Download + Manual Upload">
          <p className="text-sm text-gray-600 mb-4">
            Download the manifest and manually upload it into Excel. Works on any platform.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>Click <strong>"Download Manifest"</strong> above to save the XML file.</li>
            <li>Open Excel and go to the <strong>Insert</strong> tab.</li>
            <li>Click <strong>Get Add-ins</strong> (or <strong>Office Add-ins</strong>).</li>
            <li>Click <strong>Upload My Add-in</strong> (you may need to click "More Add-ins" first).</li>
            <li>Browse to the downloaded manifest file and click <strong>Upload</strong>.</li>
            <li>The Calculom add-in will appear in your ribbon.</li>
          </ol>
        </AccordionCard>

        {/* 5. Network Share (Windows only) */}
        {platform !== 'mac' && (
          <AccordionCard title="5. Network Share" badge="Windows Only" badgeColor="gray">
            <p className="text-sm text-gray-600 mb-4">
              For enterprise environments, host the manifest on a network share and configure Excel to trust it.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>Download the manifest file and place it on a shared network folder (e.g., <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">\\server\share\calculom\</code>).</li>
              <li>Open Excel and go to <strong>File &gt; Options &gt; Trust Center &gt; Trust Center Settings</strong>.</li>
              <li>Click <strong>Trusted Add-in Catalogs</strong>.</li>
              <li>In the <strong>Catalog URL</strong> field, enter the network share path and click <strong>Add Catalog</strong>.</li>
              <li>Check the <strong>Show in Menu</strong> box, then click <strong>OK</strong>.</li>
              <li>Restart Excel. Go to <strong>Insert &gt; My Add-ins &gt; Shared Folder</strong>. Select Calculom and click <strong>Add</strong>.</li>
            </ol>
          </AccordionCard>
        )}
      </div>

      {/* Troubleshooting */}
      <div className="card">
        <h2 className="font-medium text-gray-900 mb-4">Troubleshooting</h2>
        <div className="space-y-4">
          <FaqItem
            question="The add-in doesn't appear after sideloading"
            answer="Make sure the manifest file is in the correct folder and restart Excel completely. On Windows, check Task Manager to ensure all Excel processes are closed before reopening."
          />
          <FaqItem
            question="I get a 'manifest not valid' error"
            answer="Ensure you're using the latest manifest URL from this page. If you downloaded the file, try re-downloading it. The manifest URL must be accessible from your network."
          />
          <FaqItem
            question="Custom functions don't appear in the formula bar"
            answer="Custom functions (CLM.* namespace) may take a moment to register after first load. Try typing =CLM. in a cell — autocomplete should show available functions. If not, close and reopen the add-in task pane."
          />
          <FaqItem
            question="The add-in loads but shows a login error"
            answer="Make sure you have an active Calculom account and your subscription is current. Check your internet connection and try signing out and back in."
          />
          <FaqItem
            question="How do I update the add-in?"
            answer="The add-in updates automatically. It always loads the latest version from the Calculom server. If you need to force a refresh, clear the Office cache: on Windows, delete the contents of %LOCALAPPDATA%\Microsoft\Office\16.0\Wef\."
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FAQ ITEM
// ============================================================

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left py-1"
      >
        <span className="text-sm font-medium text-gray-800">{question}</span>
        <svg
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <p className="mt-2 text-sm text-gray-600">{answer}</p>
      )}
    </div>
  );
}
